import React from 'react';
import Image from 'components/Image';
import Link from 'next/link';
import Card from 'ui/src/components/Card';
import { twMerge } from 'utilities/src/twMerge';
import { parseImageUrl } from 'utils/imageUtils';
import extractText from 'utilities/src/extractText';
import { BlogCardFragment } from 'gql/graphql-operations';
import { getCuttedString } from 'utils/textUtils';
import ButtonLink from 'ui/src/components/ButtonLink';
import CardAuthor from './CardAuthor';
import styles from './BlogCard.module.scss';

export type BlogCardProps = {
  post: BlogCardFragment;
  withSummary?: boolean;
  withCategories?: boolean;
  imageSizes?: string;
  withAuthor?: boolean;
  withAuthorAvatar?: boolean;
  CustomHeadingTag?: keyof Pick<JSX.IntrinsicElements, 'h1' | 'h2' | 'h3'>;
  isFullImage?: boolean;
  isFirst?: boolean;
};

function BlogCard({
  post,
  withSummary,
  withCategories,
  imageSizes,
  withAuthor,
  withAuthorAvatar = true,
  CustomHeadingTag,
  isFullImage = false,
  isFirst = false,
}: BlogCardProps) {
  const featuredImage = post.featuredImage?.node ?? null; // Design should provide fallback img to place here.
  const categoryName = post.categories?.nodes[0].name ?? '';
  const cuttedPostTitle = getCuttedString({ text: post?.title || '', maxNumberOfWords: 3 });

  return (
    <Card classes={twMerge('flex flex-col h-full', { 'bg-transparent': isFullImage })}>
      <Card.Image classes="group peer">
        {featuredImage ? (
          <Link
            href={`/${post.slug}`}
            aria-label={post.title ?? ''}
            className={twMerge('relative block', isFullImage ? 'h-60 md:h-44' : 'aspect-video')}
          >
            <Image
              src={featuredImage.mediaDetails?.file ?? ''}
              sizes={imageSizes}
              alt={featuredImage.altText || `${categoryName} - ${cuttedPostTitle} `}
              title={featuredImage.altText || 'Featured image'}
              fill
              className={twMerge('object-cover', { 'brightness-50': isFullImage })}
            />
            {isFullImage && (
              <span
                className={twMerge(
                  'text-site-neutrals-0 site-paragraph-xl mdS:max-w-[15.625rem] absolute bottom-3 w-full max-w-[8rem] font-bold group-hover:underline',
                  isFirst ? 'left-3 text-left' : 'right-3 text-right',
                )}
              >
                {post.title}
              </span>
            )}
          </Link>
        ) : (
          <span />
        )}
      </Card.Image>
      {!isFullImage && (
        <Card.Container classes="h-full px-6">
          {withCategories && (
            <Card.Category key={post.categories?.nodes?.[0]?.databaseId}>
              <Link href={`/category/${post.categories?.nodes?.[0]?.slug}/`}>
                {post.categories?.nodes?.[0]?.name}
              </Link>
            </Card.Category>
          )}
          <Card.Title
            wrapperClasses={twMerge({ 'mb-2': withSummary }, { 'mb-8': !withSummary })}
            CustomHeadingTag={CustomHeadingTag}
            classes="text-site-neutrals-600 site-paragraph-xl font-bold"
          >
            <Link href={`/${post.slug}`} className="hover:underline">
              {post.title}
            </Link>
          </Card.Title>
          {withSummary && <Card.Summary classes={styles.clamp}>{extractText(post.excerpt) ?? ''}</Card.Summary>}
          <Card.Footer className={!withAuthorAvatar || !withAuthor ? 'h-[66px]' : ''}>
            {withAuthor && (
              <CardAuthor
                withAuthorAvatar={withAuthorAvatar}
                // Remove comment when fix images from wp
                // imageUrl={parseImageUrl(post.author?.node?.avatar?.url)}
                imageUrl={parseImageUrl(
                  post.author?.node?.customFields?.authorImage ?? post.author?.node?.avatar?.url ?? '',
                )}
                name={post.author?.node?.name ?? ''}
              />
            )}
            <Card.MinutesRead
              classes={twMerge({ 'w-full': !withAuthor }, 'text-site-neutrals-300')}
              readingTime={post?.timeToRead || 10}
            />
          </Card.Footer>
        </Card.Container>
      )}
      {isFullImage && (
        <ButtonLink
          href={`/${post.slug}`}
          classes={twMerge('peer-hover:border-current', { 'self-end': !isFirst })}
          variant="TextOnly"
          variantColor="Black"
          iconName={isFirst ? 'ArrowBack' : 'ArrowForward'}
          iconPosition={isFirst ? 'left' : 'right'}
          size="Medium"
        >
          {isFirst ? 'Previous article' : 'Next article'}
        </ButtonLink>
      )}
    </Card>
  );
}

BlogCard.defaultProps = {
  withSummary: false,
  withCategories: false,
};

export default BlogCard;
